import { AdvancedEventEmitter, EventBroker } from '../EventBrokers';
import { insertAt, move, removeItem } from '../arrayTools';
import { createSafeRandomId } from '../createSafeRandomId';
import { PropEventBrokerCollection } from './PropEventBrokerCollection';
import { isNumber } from 'lodash';
export class EditableEntity extends AdvancedEventEmitter {
    constructor(initialId) {
        super();
        this.propEventBrokerCollection = new PropEventBrokerCollection();
        this.collections = [];
        this.subEntities = [];
        this.handleSubEntityEvent = (data) => {
            this.propertyChanged.emit({ _this: this, propName: data._this.getId() });
        };
        this.id = initialId;
        this.idChanged = new EventBroker(this);
        this.tempId = createSafeRandomId();
        this.propertyChanged = new EventBroker(this);
        this.propEventBrokerCollection.onAnyPropChanged.registerEnsureOnce((changedProp) => {
            this.propertyChanged.emit({ _this: this, propName: changedProp });
        });
    }
    createEntityCollection(name, initialItems) {
        const collection = new CreatableEntityCollection(name, initialItems);
        this.collections.push(collection);
        this.combineEntityEvents(collection);
        return collection;
    }
    checkId(id) {
        return this.id === id || this.tempId === id;
    }
    setId(newId) {
        const oldId = this.getId();
        if (oldId !== newId) {
            this.id = newId;
            this.idChanged.emit({
                oldId,
                newId
            });
        }
    }
    getId() {
        return this.id ?? this.tempId;
    }
    isNew() {
        return this.id === undefined;
    }
    detachEntityEvents(entity) {
        entity.propertyChanged.unRegister(this.handleSubEntityEvent);
    }
    combineEntityEvents(entity) {
        entity.propertyChanged.reo(this.handleSubEntityEvent);
    }
    removeSubEntity(entity) {
        removeItem(this.subEntities, entity);
        this.detachEntityEvents(entity);
    }
    addSubEntity(entity) {
        this.subEntities.push(entity);
        this.combineEntityEvents(entity);
    }
    restore() {
        this.propEventBrokerCollection.restore();
    }
    commit() {
        this.propEventBrokerCollection.commit();
        this.collections.forEach(collection => collection.commit());
    }
    createPropEventBroker(propName, initialValue, options) {
        return this.propEventBrokerCollection.createPropEventBroker(propName, this, initialValue, options);
    }
    isChanged() {
        const hasPropChanges = this.propEventBrokerCollection.hasChanges();
        const hasCollectionChanges = this.collections.some(collection => collection.isChanged());
        return hasPropChanges || hasCollectionChanges;
    }
    getChangedProps() {
        const thisChangedProps = this.propEventBrokerCollection.getChangedProps();
        const selfChangedProps = {
            entityId: this.getId(),
            entityName: this.getEntityName(),
            changedProps: thisChangedProps
        };
        const subEntitiesChangedProps = [...this.subEntities, ...this.collections].flatMap(subEntity => {
            const collectionChangedProps = subEntity.getChangedProps();
            return collectionChangedProps.map(subEntityChangedProps => {
                if (!subEntityChangedProps.parentEntityId) {
                    return { ...subEntityChangedProps, parentEntityId: this.getId() };
                }
                return subEntityChangedProps;
            });
        });
        return [selfChangedProps, ...subEntitiesChangedProps].filter(subEntityChangedProps => subEntityChangedProps.changedProps.length > 0);
    }
    getChangeablePropsCount() {
        return this.propEventBrokerCollection.getChangeablePropsCount();
    }
}
export class CreatableEntityCollection extends EditableEntity {
    constructor(name, items) {
        super(name);
        this.name = name;
        this.handleIdChanged = (data) => {
            const currentOrder = this.getOrder();
            const newTabsOrder = currentOrder.map(id => (id === data.oldId ? data.newId : id));
            const item = this.getItem(data.newId);
            this.order.set(newTabsOrder);
            this.itemIdChanged.emit({ item, data });
        };
        this.handleItemPropertyChanged = (data) => {
            this.itemChanged.emit({ item: data._this });
        };
        this.attachItemEvents = (item) => {
            this.combineEntityEvents(item);
            item.idChanged.reo(this.handleIdChanged);
            item.propertyChanged.reo(this.handleItemPropertyChanged);
        };
        this.items = items;
        this.order = this.createPropEventBroker('order', items?.map(item => item.getId()));
        this.itemChanged = new EventBroker(this);
        this.itemIdChanged = new EventBroker(this);
        this.itemRemoved = new EventBroker(this);
        this.itemsAdded = new EventBroker(this);
        this.attachEvents();
    }
    getEntityName() {
        return this.name;
    }
    getPagedItems(page) {
        const orderedItems = this.getOrderedItems();
        const startIndex = page.offset * page.limit;
        const endIndex = startIndex + page.limit;
        return orderedItems.slice(startIndex, endIndex);
    }
    removeAllItems() {
        const allIds = this.items.map(item => item.getId());
        allIds.forEach(id => {
            this.removeItem(id);
        });
    }
    replaceAllItems(items) {
        this.removeAllItems();
        this.addItems(undefined, ...items);
    }
    getOrderedItems() {
        const allItems = this.getAllItems() ?? [];
        const order = this.order.get() ?? [];
        return order.map(id => {
            const orderedItem = allItems.find(item => item.checkId(id));
            if (!orderedItem) {
                throw new Error(`getOrderedItems - item not found for id: ${id}`);
            }
            return orderedItem;
        });
    }
    getItemAt(index) {
        const order = this.order.get() ?? [];
        const itemId = order[index];
        return this.getItem(itemId);
    }
    validateItemsDoNotExist(items) {
        items.forEach(item => {
            const itemExists = this.getItem(item.getId());
            if (itemExists) {
                throw new Error(`Item with id ${item.getId()} already exists`);
            }
        });
    }
    addBase(options, ...items) {
        if (items.length) {
            const insertStartIndex = (options?.startIndex ?? this.items.length);
            const shouldReplace = options?.replaceItemInExistingIndices ?? false;
            const keepExisting = options?.keepExistingItems ?? false;
            const itemsOrder = items.map(s => s.getId());
            let itemsToAdd = items;
            if (shouldReplace) {
                const toIndex = insertStartIndex + items.length;
                const itemsToRemove = [];
                if (keepExisting) {
                    itemsToAdd = items.filter(item => {
                        const itemId = item.getId();
                        const itemsExists = !!this.getItem(itemId);
                        if (itemsExists) {
                            const removedIndex = this.getItemOrderIndex(itemId);
                            // Here we manipulate the actual array instance so no event will be raised.
                            this.getOrder().splice(removedIndex, 1);
                        }
                        return !itemsExists;
                    });
                }
                for (let currentIndex = insertStartIndex; currentIndex < toIndex; currentIndex++) {
                    const item = this.getItemAt(currentIndex);
                    itemsToRemove.push(item);
                }
                itemsToRemove.forEach(item => {
                    if (item) {
                        this.removeItem(item.getId());
                    }
                });
            }
            this.validateItemsDoNotExist(itemsToAdd);
            this.items = [...this.items, ...itemsToAdd];
            const newSectionsOrder = insertAt(this.getOrder(), insertStartIndex, ...itemsOrder);
            this.order.set(newSectionsOrder);
            itemsToAdd.forEach(item => {
                this.attachItemEvents(item);
            });
            this.itemsAdded.emit({ items: itemsToAdd });
        }
    }
    replaceItems(startIndex, ...entities) {
        this.addBase({ startIndex, replaceItemInExistingIndices: true, keepExistingItems: true }, ...entities);
    }
    addItems(options, ...entities) {
        this.addBase(options, ...entities);
    }
    appendItem(item) {
        this.addBase(undefined, item);
    }
    moveItem(itemId, newIndex) {
        const currentOrder = this.order.get();
        const currentIndex = currentOrder?.findIndex(item => item === itemId);
        const newOrder = move(currentOrder, currentIndex, newIndex);
        this.order.set(newOrder);
    }
    isEmpty() {
        return this.items.length === 0;
    }
    getOrder() {
        return this.order.get() ?? [];
    }
    getItemOrderIndex(itemId) {
        return this.order.get().findIndex(tab => tab === itemId);
    }
    removeItem(id) {
        const removedIndex = this.items.findIndex(item => item.getId() === id);
        const itemToRemove = this.items[removedIndex];
        this.detachEntityEvents(itemToRemove);
        itemToRemove.idChanged.unRegister(this.handleIdChanged);
        itemToRemove.propertyChanged.unRegister(this.handleItemPropertyChanged);
        this.items.splice(removedIndex, 1);
        const currentItemsOrder = this.getOrder();
        const newOrder = currentItemsOrder.filter(itemId => itemId !== id);
        this.order.set(newOrder);
        this.itemRemoved.emit({ item: itemToRemove });
        return itemToRemove;
    }
    attachEvents() {
        this.items.forEach(this.attachItemEvents);
    }
    getItem(id) {
        return this.items.find(item => item.getId() === id);
    }
    restore() {
        super.restore();
        this.items.forEach(item => item.restore());
    }
    commit() {
        super.commit();
        this.items.forEach(item => item.commit());
    }
    getAllItems() {
        return this.items;
    }
    getNewItems() {
        return this.items.filter(item => item.isNew());
    }
    getChangedProps() {
        const changedProps = super.getChangedProps();
        const subItemsChangedProps = this.getOrderedItems().flatMap((item, index) => {
            return item.getChangedProps().map(entityChangedProp => {
                if (!isNumber(entityChangedProp.order)) {
                    return { ...entityChangedProp, order: index };
                }
                return entityChangedProp;
            });
        });
        return [...changedProps, ...subItemsChangedProps];
    }
    isChanged() {
        const areItemsChanged = this.items.some(item => item.isChanged());
        return super.isChanged() || areItemsChanged;
    }
    getOldItems() {
        return this.items.filter(item => !item.isNew());
    }
}
//# sourceMappingURL=EditableEntity.js.map